import React from "react";
import { Box } from "@mui/material";
import { classStatus } from "./classStatus";

const STATUS_STYLE = {
  in_progress: { color: "var(--color-success)", bg: "var(--color-success-bg)" },
  upcoming: { color: "var(--color-accent-violet)", bg: "color-mix(in srgb, var(--color-accent-violet) 10%, transparent)" },
  unscheduled: { color: "rgba(31,22,51,0.6)", bg: "var(--color-surface-press-stronger)" },
  ended: { color: "rgba(31,22,51,0.6)", bg: "var(--color-surface-press-stronger)" },
};

const Svg = ({ children }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

function StatusPill({ status }) {
  const style = STATUS_STYLE[status.kind];
  return (
    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
      <Box
        data-testid="class-status"
        sx={{ display: "inline-flex", alignItems: "center", gap: "6px", px: "10px", py: "3px", borderRadius: "999px", bgcolor: style.bg, color: style.color, fontSize: "11.5px", fontWeight: 700 }}
      >
        {status.kind === "in_progress" && (
          <Box component="span" aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: style.color, boxShadow: `0 0 0 3px color-mix(in srgb, ${style.color} 25%, transparent)` }} />
        )}
        {status.label}
      </Box>
      {status.detail && <Box sx={{ fontSize: "11.5px", color: "rgba(31,22,51,0.6)", mt: "3px" }}>{status.detail}</Box>}
    </Box>
  );
}

/**
 * One class in an assign dialog: checkbox, title and number, the instructor, and a status pill that says whether the
 * class is in progress, when it starts, or has ended. `assigned` rows are already on the item and cannot be picked.
 */
export default function AssignClassRow({ course, selected, assigned, onToggle, now }) {
  const status = classStatus(course, now);
  const active = assigned || selected;
  const tick = assigned ? "var(--color-success)" : "var(--color-primary)";
  return (
    <Box
      role="checkbox"
      aria-checked={active}
      aria-disabled={assigned}
      tabIndex={assigned ? -1 : 0}
      data-testid={`assign-class-${course.id}`}
      onClick={() => !assigned && onToggle(course.id)}
      onKeyDown={(e) => {
        if (!assigned && (e.key === " " || e.key === "Enter")) {
          e.preventDefault();
          onToggle(course.id);
        }
      }}
      sx={{
        display: "flex", alignItems: "center", gap: "13px", p: "11px 12px", borderRadius: "12px",
        cursor: assigned ? "default" : "pointer", opacity: assigned ? 0.6 : 1,
        bgcolor: selected ? "color-mix(in srgb, var(--color-primary) 6%, transparent)" : "transparent",
        "&:hover": { bgcolor: assigned ? "transparent" : "var(--color-surface-press-light)" },
      }}
    >
      <Box sx={{ width: 22, height: 22, borderRadius: "7px", flexShrink: 0, display: "grid", placeItems: "center", border: `2px solid ${active ? tick : "var(--color-hairline-cloud)"}`, bgcolor: active ? tick : "var(--color-surface-canvas-light)", color: "var(--color-surface-canvas-light)" }}>
        {active && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontSize: "14px", fontWeight: 700, color: "var(--color-ink)" }}>{course.title}</Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "2px 14px", fontSize: "12px", color: "rgba(31,22,51,0.6)" }}>
          {course.courseNumber && <span>{course.courseNumber}</span>}
          <Box component="span" data-testid="class-instructor" sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
            <Svg><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></Svg>
            {course.instructorName || "No instructor assigned"}
          </Box>
        </Box>
      </Box>
      {assigned ? (
        <Box sx={{ fontSize: "11px", fontWeight: 700, color: "var(--color-success)", bgcolor: "var(--color-success-bg)", px: "9px", py: "3px", borderRadius: "999px", flexShrink: 0 }}>Assigned</Box>
      ) : (
        <StatusPill status={status} />
      )}
    </Box>
  );
}
